/* eslint-disable react/jsx-curly-newline */
import React, { useState, useEffect, useCallback } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { UseFormReturn, Controller } from "react-hook-form";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// region Imports - Pages
import { IFormConcretingRequestInputs } from "@pages/ConcretingRequest";
// endregion Imports - Pages
// region Imports - molecules
import { BaseCard, BaseCardHeader } from "@molecules/index";
// endregion Imports - molecules
// region Imports - Hooks
import { useSelectClient } from "@atoms/SelectClient";
import { useAuth } from "@hooks/useAuth";
// endregion Imports - Hooks
// region Imports - GraphQL
import {
  TContractAllWithPaginationQueries,
  contractAllWithPagination
} from "@graphql/groups/contract/query.graphql";
import {
  ContractQueriesGetAllWithPaginationArgs,
  ContractType,
  ECommandResultStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  ConcretingRequestType
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Imports - GraphQL
// region Imports - Languages
import useTranslation from "@languages/useTranslation";
import { AlertMessages, MuiMessages } from "@languages/interfaces";
import { BaseCardMessages } from "src/languages/interfaces";
// endregion Imports - Languages
// region Imports - Helpers
import { handleErrorMessage } from "@helpers/index";
// endregion Imports - Helpers
// region Imports - Constants
import { ModulesID } from "src/store/constants";
// endregion Imports - Constants
// region Imports - Libraries
import { TextField, Autocomplete, CircularProgress } from "@libraries/mui";

import AlertContentByClient from "@atoms/AlertContentByClient";
import * as Styled from "./styles";
// endregion Imports - Libraries

// region Interfaces
interface FormConcretingRequestContractProps {
  controlForms: UseFormReturn<IFormConcretingRequestInputs>;
  currentConcretingRequest: ConcretingRequestType | undefined;
  mode: "CREATE" | "EDIT";
}
// endregion Interfaces

const FormConcretingRequestContract: React.FC<
  FormConcretingRequestContractProps
> = ({ controlForms, currentConcretingRequest, mode }) => {
  // region Hooks
  const { t } = useTranslation();
  const { value: selectedClient } = useSelectClient();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [selectedContract, setSelectedContract] = useState<ContractType>();
  const [openContractAutocomplete, setOpenContractAutocomplete] = useState(false);
  const [optionsContractAutocomplete, setOptionsContractAutocomplete] = useState<ContractType[]>([]);

  const [noPermissionInCurrentClient, setNoPermissionInCurrentClient] = useState<boolean>(false);

  const [whereContractIds, setWhereContractIds] = useState<string>("");
  // endregion States
  // region Forms
  const {
    control,
    setValue,
    getValues,
    formState: { errors: formErrors }
  } = controlForms;
  // endregion Forms
  // region Constants
  const loadingContractAutocomplete = openContractAutocomplete && optionsContractAutocomplete.length === 0;
  // endregion Constants
  // region GraphQL - Queries
  // query for permissions
  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<
  TPermissionAllWithoutPaginationQueries,
  PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.id"]), {
    fetchPolicy: "network-only",
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONCRETING_REQUEST
        }
      ]
    }
  });

  // query for contracts
  const [
    getContracts,
    { loading: loadingContracts, error: errorContracts, data: dataContracts }
  ] = useLazyQuery<
    TContractAllWithPaginationQueries,
    ContractQueriesGetAllWithPaginationArgs
  >(
    contractAllWithPagination([
      "id",
      "status",
      "code",
      "contractDate",
      "construction.name",
      "construction.address",
      "construction.county",
      "construction.number",
      "construction.complement",
      "contractSellers.seller.name"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );
  // endregion GraphQL - Queries
  // region Functions
  const handleSelectContract = useCallback((contract: ContractType) => {
    setSelectedContract(contract);
  }, []);

  const loadContracts = useCallback(async () => {
    const response = dataContracts?.contract.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const contractsSorted = response.result?.sort((a, b) => (a?.contractDate < b?.contractDate ? 1 : -1));

      setOptionsContractAutocomplete(contractsSorted as ContractType[]);
    }
  }, [dataContracts]);

  const makeWhereContractIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      if (response?.result?.length === 0) {
        setNoPermissionInCurrentClient(true);

        return;
      }

      setNoPermissionInCurrentClient(false);

      const contractIds: string[] = [];

      response.result?.forEach((permission) => {
        contractIds.push(permission?.contract?.id || "");
      });

      const contractIdsStr = contractIds
        .filter((id) => id && id !== "")
        .join("|");

      if (!contractIdsStr || contractIdsStr === "") {
        return;
      }

      setWhereContractIds(contractIdsStr);
    }
  }, [dataPermissions]);
  // endregion Functions
  // region UseEffects

  useEffect(() => {
    if (whereContractIds !== "") {
      getContracts({
        variables: {
          where: [
            {
              property: "id",
              operator: EWhereClausesOperators.IN,
              value: whereContractIds
            }
          ]
        }
      });
    }
  }, [getContracts, whereContractIds]);

  useEffect(() => {
    if (dataPermissions) makeWhereContractIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereContractIds]);

  useEffect(() => {
    if (dataContracts) loadContracts();
    if (errorContracts) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContracts.message
      });
    }
  }, [addToast, dataContracts, errorContracts, loadContracts]);

  useEffect(() => {
    if (!currentConcretingRequest) return;

    setValue("contract", currentConcretingRequest?.contract);
    setSelectedContract(currentConcretingRequest?.contract as ContractType);
  }, [currentConcretingRequest, setValue]);
  // endregion UseEffects

  return (
    <Styled.FormContainer>
      {!noPermissionInCurrentClient && (
      <Controller
        name="contract"
        control={control}
        defaultValue={null}
        render={({ field }) => (
          <Autocomplete
            {...field}
            onChange={(_ev, item) => {
              field.onChange(item);
              handleSelectContract(item);
            }}
            onOpen={() => setOpenContractAutocomplete(true)}
            onClose={() => setOpenContractAutocomplete(false)}
            isOptionEqualToValue={(option, valueOption) => option.id === valueOption.id
                }
            getOptionLabel={(option) => `${option.code} | ${option.construction.name}`
                }
            open={openContractAutocomplete}
            options={optionsContractAutocomplete}
            loading={loadingContractAutocomplete}
            loadingText={t(MuiMessages.autocompleteLoading)}
            noOptionsText={t(MuiMessages.autocompleteNoOptions)}
            autoHighlight
            disabled={mode === "EDIT"}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(BaseCardMessages.contractLabelText)}
                error={!!formErrors.contract}
                helperText={handleErrorMessage(formErrors.contract, t)}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingContractAutocomplete && (
                      <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        )}
      />
      )}
      {!noPermissionInCurrentClient && selectedContract && (
      <Styled.ContractCardContainer>
        <BaseCard
          id={selectedContract.id}
          header={<BaseCardHeader type="contract" data={selectedContract} />}
        />
      </Styled.ContractCardContainer>
      )}
      {noPermissionInCurrentClient && (
        <AlertContentByClient
          alertMessage={noPermissionInCurrentClient
            ? t(AlertMessages.noPermissionConcretingRequestSpecificClient)
            : t(AlertMessages.noHaveConcretingRequestSpecificClient)}
          iconColor={noPermissionInCurrentClient ? "red" : "yellow"}
        />
      )}
    </Styled.FormContainer>
  );
};

export default FormConcretingRequestContract;
